import "../styles/CommitDetails.css";
import React from "react";
import { useLoaderData, useRouteLoaderData } from "react-router-dom";
import { MessageBar, MessageBarType, initializeIcons } from "@fluentui/react";
import { ApiHelper } from '../ApiHelper';
import { useConfig } from "../configContext";
import { RegressionDiagnosisPanel } from "./regression-diagnosis-panel";
import { getRegressionDiagnosis } from "./regression-diagnosis-api";
import FarmCards from "./FarmCards";
import type {
    CommitDetails as CommitDetailsModel,
    Regression,
} from "../interfaces/CommitDetails";
import type { RegressionDiagnosis } from "../interfaces/RegressionDiagnosis";
import type { Service } from "../interfaces/Service";
import type { RegressionEmailContext } from "../features/regression-email/create-regression-email";
import { createRegressionChartImages } from "../features/regression-email/create-regression-chart-images";

interface RegressionMessageBarProps {
    serviceName: string;
    regressions: Regression[];
}

interface CommitDetailsLoaderData {
    serviceName: string;
    commitDetails: CommitDetailsModel | null;
    regressionDiagnosis: RegressionDiagnosis | null;
    errorMessage: string | null;
}

interface RegressionGroupProcessInfo {
    id: number;
    name: string;
}

interface RootUserInfo {
    name?: string | null;
    emailAddress?: string | null;
}

type RegressionGroups = Record<string, Record<string, RegressionGroupProcessInfo[]>>;

declare global {
    interface Window {
        cachedCommitUrl?: string;
        cachedCommit?: CommitDetailsModel | null;
    }
}

export function RegressionMessageBar({ serviceName, regressions }: RegressionMessageBarProps) {
    initializeIcons();
    const config = useConfig();
    const service = config.services.find((candidate: Service) => candidate.name === serviceName);

    if (!service) {
        return null;
    }

    const environments = service.Environments ?? [];
    const processes = service.Processes ?? [];

    const regressionsByEnvAndMetric = regressions
        .filter((regression) => regression.source === 2 && regression.isRegression)
        .reduce<RegressionGroups>((acc, regression) => {
            const environment = environments.find((candidate) => candidate.id === regression.environmentId);
            const envName = environment?.name || "Unknown Environment";
            const envId = environment?.id ?? 999999;

            const process = processes.find((candidate) => candidate.id === regression.processId);
            const processName = process?.friendlyName || "Unknown Process";
            const processId = process?.id ?? 999999;

            const metricProcess = processes.find((candidate) =>
                Object.values(candidate.metrics).some((metric) => metric.id === regression.metricType),
            );
            const metricName = metricProcess
                ? Object.values(metricProcess.metrics).find((metric) => metric.id === regression.metricType)?.name || "Unknown Metric"
                : "Unknown Metric";
            const metricId = regression.metricType ?? 999999;

            const envKey = `${envId}|||${envName}`;
            const metricKey = `${metricId}|||${metricName}`;

            acc[envKey] = acc[envKey] || {};
            acc[envKey][metricKey] = acc[envKey][metricKey] || [];

            const processInfo = { id: processId, name: processName };
            if (!acc[envKey][metricKey].find((candidate) => candidate.id === processId)) {
                acc[envKey][metricKey].push(processInfo);
            }

            return acc;
        }, {});

    const sortByIdFromKey = ([a]: [string, unknown], [b]: [string, unknown]) =>
        parseInt(a.split('|||')[0], 10) - parseInt(b.split('|||')[0], 10);

    const sortedEnvironments = Object.entries(regressionsByEnvAndMetric).sort(sortByIdFromKey);

    return (
        <MessageBar messageBarType={MessageBarType.severeWarning} isMultiline>
            <div className="regression-message">
                <div className="regression-title">Regression detected in this commit</div>
                <div className="regression-environments">
                    {sortedEnvironments.map(([envKey, metrics]) => {
                        const envName = envKey.split('|||')[1];
                        const sortedMetrics = Object.entries(metrics).sort(sortByIdFromKey);

                        return (
                            <div key={envKey} className="regression-environment">
                                <strong>{envName}:</strong>
                                <ul className="regression-metrics">
                                    {sortedMetrics.map(([metricKey, relatedProcesses]) => {
                                        const metricName = metricKey.split('|||')[1];
                                        const processNames = relatedProcesses
                                            .sort((a, b) => a.id - b.id)
                                            .map((processInfo) => processInfo.name)
                                            .join(', ');

                                        return (
                                            <li key={metricKey} className="regression-metric-item">
                                                {metricName}: {processNames}
                                            </li>
                                        );
                                    })}
                                </ul>
                            </div>
                        );
                    })}
                </div>
            </div>
        </MessageBar>
    );
}

export function CommitDetails() {
    const { serviceName, commitDetails, regressionDiagnosis, errorMessage } = useLoaderData() as CommitDetailsLoaderData;
    const userInfo = useRouteLoaderData("root") as RootUserInfo | null | undefined;
    const config = useConfig();
    const service = config.services.find((candidate: Service) => candidate.name === serviceName);

    window.document.title = 'PR Guardian - ' + (commitDetails ? commitDetails.commitMetaData.title : 'Error');

    if (errorMessage || !commitDetails) {
        return (
            <div className="page-container">
                <div className="no-commits-message">
                    {errorMessage ?? 'Oops, sorry. There are no commits to see :/'}
                </div>
            </div>
        );
    }

    return (
        <div className="page-container">
            {commitDetails.commitMetaData.regressionMask > 0 && (
                <RegressionMessageBar serviceName={serviceName} regressions={commitDetails.regressions} />
            )}
            <RegressionDiagnosisPanel
                serviceName={serviceName}
                commitId={commitDetails.commitId}
                initialDiagnosis={regressionDiagnosis}
                azdoOrg={service?.azDOOrg}
                emailContext={buildRegressionEmailContext(
                    serviceName,
                    commitDetails,
                    service,
                    userInfo,
                )}
                createEmailImages={() => createRegressionChartImages(commitDetails, service)}
            />
            <FarmCards serviceName={serviceName} commitDetails={commitDetails} />
        </div>
    );
}

function buildRegressionEmailContext(
    serviceName: string,
    commitDetails: CommitDetailsModel,
    service: Service | undefined,
    userInfo: RootUserInfo | null | undefined,
): RegressionEmailContext {
    const metadata = commitDetails.commitMetaData;
    const environmentNames = [...new Set(
        (commitDetails.regressions ?? [])
            .filter(regression => regression.source === 0 && regression.isRegression)
            .map(regression => Object.values(service?.environments ?? {})
                .find(environment => environment.id === regression.environmentId)?.name)
            .filter((name): name is string => Boolean(name)),
    )];

    return {
        serviceName,
        commitId: commitDetails.commitId,
        prId: metadata.pRId ?? metadata.prId ?? metadata.prid ?? null,
        commitTitle: metadata.title ?? null,
        authorName: metadata.authorName ?? null,
        authorEmail: metadata.authorEmail ?? null,
        mergeDate: metadata.pRMergeDate ?? metadata.prMergeDate ?? metadata.prmergeDate ?? null,
        environmentNames,
        prGuardianUrl: window.location.href,
        senderName: userInfo?.name ?? null,
        senderEmail: userInfo?.emailAddress ?? null,
    };
}

export async function commitDetailsLoader(serviceName: string, commitId: string): Promise<CommitDetailsLoaderData> {
    const apiUrl = `/api/service/${serviceName}/commits/${commitId}/details`;
    let commitDetails: CommitDetailsModel | null = null;
    let errorMessage: string | null = null;

    try {
        if (window.cachedCommitUrl !== apiUrl) {
            commitDetails = await ApiHelper<CommitDetailsModel>(apiUrl);
            patchCommitDetails(commitDetails);
            window.cachedCommit = commitDetails;
            window.cachedCommitUrl = apiUrl;
        } else {
            commitDetails = window.cachedCommit ?? null;
        }
    } catch (error) {
        errorMessage = 'Oops, sorry. There are no commits to see :/';
    }

    let regressionDiagnosis: RegressionDiagnosis | null = null;
    if (commitDetails) {
        try {
            regressionDiagnosis = await getRegressionDiagnosis(serviceName, commitId);
        } catch (error) {
            regressionDiagnosis = null;
        }
    }

    return { serviceName, commitDetails, regressionDiagnosis, errorMessage };
}

export function checkIfRegression(metadata: { isManualRegression: boolean | null; isRegression: boolean }) {
    if (metadata.isManualRegression !== null) {
        return metadata.isManualRegression;
    }
    return metadata.isRegression;
}

const getMax = (currentMax: number, point: { metricValue: number }) => Math.max(currentMax, point.metricValue);

export function patchCommitDetails(details: CommitDetailsModel) {
    if (details.commitMetaData) {
        details.commitId = details.commitMetaData.commitId;
    }

    details.max = {};
    for (const metric of details.commitMetrics ?? []) {
        for (const farmMetric of metric.farmMetrics ?? []) {
            for (const metricSubType of farmMetric.metricSubTypes ?? []) {
                const subTypeMax = (metricSubType.commitTimeSeries?.data ?? []).reduce(getMax, 0);
                if (!details.max[farmMetric.env] || subTypeMax > details.max[farmMetric.env]) {
                    details.max[farmMetric.env] = subTypeMax;
                }
            }
        }
    }
}
